/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Mobile Navigation Drawer (Slide-Over Sheet) (Task 10.1.2 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect } from 'react';
import { useUiStore } from '../../stores/useUiStore';
import { useTheme } from '../../context/ThemeProvider';
import { useI18n } from '../../stores/useI18nStore';
import { NAVIGATION_SECTIONS, type NavigationItem } from '../../navigation/navigation-items';
import { X, ExternalLink } from 'lucide-react';
import { LOGO_VARIANTS } from '@sofia/assets';
import { SophiaIcon } from '@sofia/assets';

export interface MobileDrawerProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  isOpen?: boolean;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({ currentPath, onNavigate, isOpen: isOpenProp }) => {
  const uiMobileDrawerOpen = useUiStore((state) => state.mobileDrawerOpen);
  const setMobileDrawerOpen = useUiStore((state) => state.setMobileDrawerOpen);
  const { themeData, isDark } = useTheme();

  const mobileDrawerOpen = typeof isOpenProp === 'boolean' ? isOpenProp : uiMobileDrawerOpen;
  const { t } = useI18n();

  const branding = themeData?.branding;
  const siteName = branding?.siteName || 'Sophia Platform';

  const fullLogoUrl =
    (isDark ? branding?.logoDarkUrl : branding?.logoLightUrl) ||
    (isDark ? LOGO_VARIANTS.fullDark : LOGO_VARIANTS.fullLight);

  // Listen for Escape key to close mobile drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileDrawerOpen) {
        setMobileDrawerOpen(false);
      }
    };

    if (mobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileDrawerOpen, setMobileDrawerOpen]);

  if (!mobileDrawerOpen) {
    return null;
  }

  const handleItemClick = (item: NavigationItem) => {
    setMobileDrawerOpen(false);
    if (item.isExternal) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      onNavigate(item.path);
    }
  };

  return (
    <div className="md:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      {/* 1. Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 animate-fade-in"
        onClick={() => setMobileDrawerOpen(false)}
        aria-hidden="true"
      />

      {/* 2. Slide-Over Drawer Container */}
      <div className="relative w-[280px] max-w-[85vw] h-full bg-surface border-r border-surface-border shadow-2xl flex flex-col z-10 animate-slide-right">
        {/* Drawer Header */}
        <div className="h-16 px-4 border-b border-surface-border flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              setMobileDrawerOpen(false);
              onNavigate('/workspace/dashboard');
            }}
            className="flex items-center gap-3 text-left focus:outline-none"
            title={siteName}
          >
            <img
              src={fullLogoUrl}
              alt={siteName}
              className="h-8 max-w-[170px] object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLElement).style.display = 'none';
                const fb = e.currentTarget.nextElementSibling as HTMLElement;
                if (fb) fb.style.display = 'flex';
              }}
            />
            <div style={{ display: 'none' }} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-fg shadow-glow-primary">
                <SophiaIcon name="hologram" size={16} className="text-primary-fg" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-extrabold text-base text-foreground tracking-tight">
                  {siteName}
                </span>
                <span className="text-[10px] text-foreground-muted font-mono tracking-wider uppercase">
                  {t('common.preview')}
                </span>
              </div>
            </div>
          </button>

          <button
            onClick={() => setMobileDrawerOpen(false)}
            className="p-2 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Navigation Sections */}
        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {NAVIGATION_SECTIONS.map((section) => {
            const sectionTitle = t(
              `nav.sections.${section.id}` as any,
              undefined,
              section.title,
            );

            return (
              <div key={section.id} className="space-y-1">
                <div className="flex items-center gap-2 px-3 py-1">
                  {section.sophiaIcon && (
                    <SophiaIcon
                      name={section.sophiaIcon}
                      size={12}
                      className="text-foreground-muted/60 shrink-0"
                    />
                  )}
                  <h4 className="text-[11px] font-semibold text-foreground-muted/70 tracking-wider uppercase">
                    {sectionTitle}
                  </h4>
                </div>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const isActive = currentPath === item.path;
                    const Icon = item.icon;
                    const itemLabel = t(
                      `nav.items.${item.id}.label` as any,
                      undefined,
                      item.label,
                    );

                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-foreground-muted hover:text-foreground hover:bg-muted/50'
                        }`}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {item.sophiaIcon ? (
                          <SophiaIcon
                            name={item.sophiaIcon}
                            size={16}
                            className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`}
                          />
                        ) : (
                          <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                        )}
                        <span className="flex-1 text-left truncate">{itemLabel}</span>
                        {item.badge && (
                          <span
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${
                              item.badgeVariant === 'accent'
                                ? 'bg-accent/15 text-accent border border-accent/20'
                                : item.badgeVariant === 'emerald'
                                ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/20'
                                : 'bg-primary/15 text-primary border border-primary/20'
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                        {item.isExternal && <ExternalLink className="w-3.5 h-3.5 text-foreground-muted" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Drawer Footer Status */}
        <div className="p-4 border-t border-surface-border text-center">
          <p className="text-[11px] text-foreground-muted font-mono">
            Sophia Platform Edge &bull; v2.0
          </p>
        </div>
      </div>
    </div>
  );
};
